import { ref, reactive, onActivated, onMounted, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Edit, Delete } from '@element-plus/icons-vue'
import { request } from '@/util/globalContext'
import { enumSelect } from '@/util/vueutil/commonList'

// 公司列表逻辑处理
export default function useCompanyList() {
    // ========== 响应式数据定义 ==========
    const companyTypeList = ref([]) // 公司类型枚举列表

    // 搜索表单（默认值初始化）
    const searchForm = reactive({
        companyId: '',
        companyName: '',
        isDeleted: '',
        createTime: '',
        updateTimeRange: []
    })

    // 列表及弹窗核心响应式数据
    const listRef = ref(null) // 列表组件引用
    const dialogVisible = ref(false) // 新增/编辑弹窗显示状态
    const dialogType = ref('add') // 弹窗类型：add-新增 edit-编辑
    const formRef = ref(null) // 表单组件引用
    const selectedRow = ref(null) // 当前选中的行数据

    // 表单默认值
    const FORM_DEFAULT = {
        companyId: '',
        companyName: '',
        companyType: '',
        contactPhone: '',
        companyAddress: '',
        miniProgramCode: '',
        isDeleted: '1'
    }
    const formData = reactive({ ...FORM_DEFAULT }) // 表单绑定数据

    // 表单校验规则
    const formRules = reactive({
        companyId: [
            { max: 50, message: '公司ID长度不能超过50个字符', trigger: 'blur' }
        ],
        companyName: [
            { required: true, message: '请输入公司名称', trigger: 'blur' },
            { max: 100, message: '公司名称长度不能超过100个字符', trigger: 'blur' }
        ],
        companyType: [{ required: true, message: '请选择公司类型', trigger: 'change' }],
        isDeleted: [{ required: true, message: '请选择是否生效', trigger: 'change' }],
        miniProgramCode: [
            { max: 200, message: '小程序编码长度不能超过200个字符', trigger: 'blur' }
        ]
    })

    // ========== 加载枚举数据 ==========
    const loadCompanyTypes = async () => {
        try {
            companyTypeList.value = await enumSelect('enumType', 'company_type')
        } catch (err) {
            console.error('获取公司类型枚举失败', err)
        }
    }

    // ========== 核心方法 - 列表加载 ==========
    // 加载公司列表（含分页、搜索参数）
    const handleLoadList = async (paginationParams) => {
        if (!listRef.value) return
        if (!listRef.value.isInited) {
            await nextTick()
            if (!listRef.value.isInited) {
                ElMessage.warning('列表组件初始化中，请稍后重试')
                return
            }
        }

        listRef.value.setLoading(true)
        try {
            const params = {
                ...paginationParams,
                ...searchForm,
                updateTimeStart: searchForm.updateTimeRange[0] || '',
                updateTimeEnd: searchForm.updateTimeRange[1] || ''
            }
            const res = await request.get('/company/getCompanyList', { params })
            if (res.code === 200) {
                const records = (res.data.records || [])
                listRef.value.updateListData(records)
                listRef.value.updatePagination({
                    pageNum: paginationParams.pageNum,
                    pageSize: paginationParams.pageSize,
                    total: res.data.total || 0
                })
            } else {
                ElMessage.error('列表加载失败：' + (res.msg || '接口异常'))
                listRef.value.updateListData([])
                listRef.value.updatePagination({ ...paginationParams, total: 0 })
            }
        } catch (error) {
            ElMessage.error('列表加载失败：网络异常')
            console.error('加载公司列表失败：', error)
            listRef.value.updateListData([])
            listRef.value.updatePagination({ ...paginationParams, total: 0 })
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // ========== 列表交互 - 行选择 ==========
    const handleSelectionChange = (val) => {
        selectedRow.value = val?.length ? val[0] : null
    }
    const handleCurrentRowChange = (val) => {
        selectedRow.value = val
    }

    // ========== 弹窗操作 - 新增/编辑 ==========
    const handleAdd = async () => {
        dialogType.value = 'add'
        formRef.value?.resetFields()
        Object.assign(formData, FORM_DEFAULT)
        await loadCompanyTypes() // 加载枚举数据
        dialogVisible.value = true
    }

    const handleEdit = async () => {
        if (!selectedRow.value) {
            ElMessage.warning('请先选中一条公司数据！')
            return
        }
        if (!listRef.value?.isInited) {
            ElMessage.warning('列表组件初始化中，请稍后重试')
            return
        }

        try {
            listRef.value.setLoading(true)
            await loadCompanyTypes() // 加载枚举数据

            const res = await request.get('/company/getCompanyList', {
                params: { companyId: selectedRow.value.companyId }
            })

            if (res.code !== 200) {
                ElMessage.error('获取公司详情失败：' + (res.msg || '接口异常'))
                return
            }
            const companyData = res.data.records[0]
            formData.companyId = companyData.companyId
            formData.companyName = companyData.companyName
            formData.companyType = companyData.companyType || ''
            formData.contactPhone = companyData.contactPhone || ''
            formData.companyAddress = companyData.companyAddress || ''
            formData.miniProgramCode = companyData.miniProgramCode || ''
            formData.isDeleted = companyData.isDeleted || '0'

            dialogType.value = 'edit'
            dialogVisible.value = true
        } catch (error) {
            ElMessage.error('获取公司详情失败' + error)
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // ========== 公司操作 - 删除 ==========
    const handleDelete = async () => {
        if (!selectedRow.value) {
            ElMessage.warning('请先选中一条公司数据！')
            return
        }
        if (selectedRow.value.isDeleted === '0') {
            ElMessage.warning('该公司状态为有效，不能删除！')
            return
        }

        try {
            await ElMessageBox.confirm(
                '确定要删除该公司吗？',
                '删除确认',
                { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
            )

            listRef.value.setLoading(true)
            const res = await request.post('/company/delCompany', { companyId: selectedRow.value.companyId })
            if (res.code === 200) {
                ElMessage.success('删除成功')
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            } else {
                ElMessage.error('删除失败：' + (res.msg || '接口异常'))
            }
        } catch (error) {
            if (error !== 'cancel') {
                ElMessage.error('删除失败：网络异常')
            }
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // ========== 表单操作 - 提交/重置 ==========
    const handleSubmit = async () => {
        if (!formRef.value) return

        const isValid = await new Promise((resolve) => {
            formRef.value.validate((valid) => {
                resolve(valid);
            });
        });
        if (!isValid) return;

        listRef.value.setLoading(true)
        try {
            const res = await request.post('/company/saveOrUpdateCompany', { ...formData, dialogType: dialogType.value })
            if (res.code === 200) {
                ElMessage.success(res.msg)
                dialogVisible.value = false
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            } else {
                const tip = dialogType.value === 'add' ? '新增失败' : '修改失败'
                ElMessage.error(tip + '：' + (res.msg || '接口异常'))
            }
        } catch (error) {
            ElMessage.error('接口异常:' + error)
        } finally {
            listRef.value.setLoading(false)
        }
    }

    const handleReset = () => {
        Object.keys(searchForm).forEach((key) => {
            searchForm[key] = Array.isArray(searchForm[key]) ? [] : ''
        })
    }

    // ========== 生命周期 ==========
    onMounted(async () => {
        await loadCompanyTypes() // 加载枚举数据
        await nextTick()
        setTimeout(() => {
            if (listRef.value?.isInited) {
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            }
        }, 50)
    })
    onActivated(async () => {
        if (listRef.value?.isInited) {
            const pagination = listRef.value.getPagination()
            handleLoadList(pagination)
        }
    })

    // ========== 暴露变量/方法 ==========
    return {
        companyTypeList,
        searchForm,
        listRef,
        dialogVisible,
        dialogType,
        formRef,
        selectedRow,
        formData,
        formRules,
        Plus, Edit, Delete,
        handleSelectionChange,
        handleCurrentRowChange,
        handleAdd,
        handleEdit,
        handleDelete,
        handleSubmit,
        handleLoadList,
        handleReset
    }
}
